/**
 * A group's screen: who is in it, who owes whom, the box you file an expense
 * in, and everything that has been spent.
 *
 * The four reads are `GET /api/groups/:id` (the group and its people),
 * `GET /api/groups/:id/balances` (a row per member, positive meaning the
 * group owes them), `GET /api/groups/:id/settlement-plan` and
 * `GET /api/groups/:id/transactions`.
 *
 * **The two things this screen is for cost no taps to reach.** Filing an
 * expense and finding out who has to pay whom were both behind a button, and
 * both are the reason somebody opened the group in the first place. So the
 * plan is read with the screen and drawn on it, and the expense sheet is the
 * screen's resting state rather than a panel that has to be summoned. What is
 * left of filing "$24.00, Pizza" is four digits, one tap into the description
 * box, five letters and the press that files it — eleven, against twelve, and
 * the only one that is not a digit, a letter or the filing is that tap into the
 * box. Autofocusing it would raise a phone keyboard over the pad, and over a
 * screen people also come to read, which is a worse trade than the tap.
 *
 * The plan used to be asked for only on request, on the grounds that it
 * invents payments between people who never transacted and that is a poor
 * thing to show unasked. The honesty that argument wanted is real, and it is
 * still here — it is `describePlanShape`, under the list, saying how many
 * payments were rerouted into how many, every time — but it is the *sentence*
 * that does that work, not the button. Hiding the answer behind a press only
 * ever cost the press.
 *
 * **Settling up is two halves, and the second one is why people believe the
 * first.** The plan says who should pay whom, with the payment count before
 * and after simplification stated either way, because a plan that quietly
 * reroutes somebody's money to a stranger is the thing that makes this kind of
 * screen distrusted; the switch that turns the rerouting off is the group's,
 * so everybody in it is reading the same list. Settling one up asks what was
 * actually paid — how much, and on which day, because a plan is a suggestion
 * and part of it may be all that changed hands — and then writes an ordinary
 * `repayment` down the same optimistic write an expense uses. The person it
 * was paid *to* is then asked to say it landed, which moves no balance at all,
 * because the balance moved when the payment was settled.
 *
 * The expense list is grouped by month by walking the rows the server already
 * ordered, never by sorting them again here. And a row filed but not yet
 * answered is drawn as itself, marked pending, rather than as a spinner: the
 * balances above it have already moved, so pretending the row is not there
 * yet would be the one inconsistency on the screen.
 *
 * Correcting a row opens the same sheet *inside that row*, where the Edit
 * button is, rather than back up in the composer: an expense from March is
 * four screens down the list, and a form that opens somewhere the person
 * cannot see is a form they have to go and find.
 *
 * Projects sit between the composer and the list (`src/components/Projects.tsx`),
 * and a row that is in one carries its name as a chip — a label, never money,
 * which is all a project is.
 */

import { useState } from 'react'
import { newIdempotencyKey, type Payment, type Transaction } from '../api'
import { Amount, AmountChip } from '../components/Amount'
import { Avatar, MemberStack } from '../components/Avatar'
import { GroupSettings } from '../components/GroupSettings'
import { People } from '../components/People'
import { Projects } from '../components/Projects'
import { Receipts } from '../components/Receipts'
import { SettleUp } from '../components/SettleUp'
import { categoryFor } from '../lib/categories'
import {
  describeMember,
  describeYourStake,
  formatDay,
  formatMoney,
  groupByMonth,
} from '../lib/format'
import {
  describeNudge,
  describePlanShape,
  describeRejection,
  settlementDraft,
} from '../lib/settle'
import { projectsById } from '../lib/projects'
import { staggerStyle } from '../motion-react'
import {
  useAwaitingSettlements,
  useCancelSettlement,
  useConfirmSettlement,
  useFileExpense,
  useGroup,
  useGroupBalances,
  useGroupTransactions,
  useMe,
  useProjects,
  useRejectSettlement,
  useSetSimplifyDebts,
  useSettlementPlan,
} from '../queries'
import type { Route } from '../routes'
import { AddExpense } from './AddExpense'

/** A 0/1 limit read the way `server/limits.ts` reads one: null is unlimited. */
function allowsUploads(limit: { value: number | null } | undefined): boolean {
  if (limit === undefined) return true
  return limit.value === null || limit.value > 0
}

export function Group({
  groupId,
  myId,
  navigate,
}: {
  groupId: string
  myId: string
  navigate: (to: Route) => void
}) {
  /**
   * The row being corrected, if any. The transaction itself rather than just
   * its id, because the sheet re-opens from the row's own `split`, which is on
   * it already; fetching it again would be a round trip for something the list
   * is holding.
   */
  const [editing, setEditing] = useState<Transaction | null>(null)
  /**
   * What makes the composer a fresh sheet again after one has been filed.
   * Filing is optimistic and the panel it used to live in simply closed; a
   * composer that is always on the screen has nowhere to go, so it is
   * re-mounted instead, which is the same thing `key` does for a row being
   * edited.
   */
  const [composer, setComposer] = useState(0)
  /**
   * Which row's receipts are open. One at a time, and by id rather than by a
   * flag on the row, so it survives the list being refetched underneath it.
   */
  const [openReceipts, setOpenReceipts] = useState<string | null>(null)
  /**
   * Which settlement's Cancel has been pressed once and is waiting to be
   * pressed again. `src/screens/Recurring.tsx` asks the same way, for the same
   * reason: the button is beside two harmless ones and it moves money.
   */
  const [cancelling, setCancelling] = useState<string | null>(null)
  /**
   * And which nudge's "No" has been pressed once. Held apart from `cancelling`
   * above even though both are a settlement id: the nudge and the row are two
   * places on this screen, and a question left open in one of them is not a
   * question open in the other.
   */
  const [rejecting, setRejecting] = useState<string | null>(null)
  /**
   * The suggested payment whose Settle up dialog is open, if any — the payment
   * itself rather than an index, because the plan refetches underneath this
   * screen and a position in a list that has just been re-ordered is a
   * different payment.
   */
  const [settling, setSettling] = useState<Payment | null>(null)

  const group = useGroup(groupId)
  const balances = useGroupBalances(groupId)
  const transactions = useGroupTransactions(groupId)
  /**
   * The group's own answer, on by default — and the plan is asked for by name
   * rather than by leaving the server to look the setting up, so that flipping
   * the switch shows the other list immediately instead of racing the write
   * that saves the preference. `src/queries.ts` says the same from the other
   * end. It is asked for unconditionally: who pays whom is the question this
   * screen exists to answer, and it should not cost a tap.
   */
  const simplify = group.data?.group.simplifyDebts ?? true
  // Held until the group itself has landed. `simplify` is the group's own
  // setting and the key carries it, so asking before it is known would fetch
  // the default answer and then immediately fetch the other one — a wasted
  // request, and a list that visibly changed under somebody who had touched
  // nothing.
  const plan = useSettlementPlan(groupId, group.data !== undefined, simplify)
  const setSimplify = useSetSimplifyDebts(groupId)
  // The nudge, and the whole point of it: it is read with the screen rather
  // than when somebody opens the settle-up panel, because its job is to be
  // seen by a person who was not looking for it.
  const awaiting = useAwaitingSettlements(groupId)
  const confirmSettlement = useConfirmSettlement(groupId)
  // The nudge's other answer. It removes the payment — see `useRejectSettlement`
  // — which is why it asks twice and why it is disabled in an archived group.
  const rejectSettlement = useRejectSettlement(groupId)
  // The undo for Settle up below. An ordinary soft delete, because a
  // settlement is an ordinary transaction — see `useCancelSettlement`.
  const cancelSettlement = useCancelSettlement(groupId)
  const fileExpense = useFileExpense(groupId, myId)
  // The storage quota and the upload switch, so the receipts panel can draw
  // the bar and hide the button before anybody picks a file. It is one request
  // for the whole screen rather than one per row.
  const me = useMe()
  // The chip on each row is named from this — the same query the Projects
  // section below reads, so it is one request for both.
  const projects = useProjects(groupId)
  const projectOf = projectsById(projects.data?.projects ?? [])

  const currency = group.data?.group.currency ?? balances.data?.group.currency ?? 'USD'
  const members = group.data?.members ?? []
  const current = members.filter((member) => member.leftAt === null)
  const nameOf = new Map(members.map((member) => [member.userId, member.displayName]))
  const archived = group.data?.group.archivedAt != null
  const isOwner = group.data?.you.role === 'owner'
  const hasTransactions = (transactions.data?.transactions.length ?? 0) > 0

  if (group.isError) {
    return (
      <section className="card">
        <h2>That group</h2>
        <p className="bad">{group.error.message}</p>
        <p className="actions">
          <button type="button" onClick={() => navigate({ name: 'home' })}>
            Back to your home screen
          </button>
        </p>
      </section>
    )
  }

  return (
    <>
      <p className="crumbs">
        <button type="button" className="button--quiet" onClick={() => navigate({ name: 'home' })}>
          ← Home
        </button>
        {/*
          The other half of a group. A receipt does not have to be attached to
          an expense — it is photographed at the till and filed that evening —
          so the pile is a place of its own rather than something reachable
          only through the expense it has not been put on yet.
        */}
        <button
          type="button"
          className="button--quiet"
          onClick={() => navigate({ name: 'receipts', groupId })}
        >
          Receipts →
        </button>
        {/*
          Rent, the internet — the expenses nobody wants to file twelve times a
          year. A rule is a template and a date, and the expenses it makes land
          in the list below like any other, so this is a place to set them up
          rather than a second list of money.
        */}
        <button
          type="button"
          className="button--quiet"
          onClick={() => navigate({ name: 'recurring', groupId })}
        >
          Repeating →
        </button>
        {/*
          What has happened, and — for an edit or a delete — what it changed.
          Its own screen rather than a panel here for the same reason the
          receipts pile is: a group's history keeps growing, and a screen
          scrolling to fit it is a screen with room for the CSV export
          beside it too.
        */}
        <button
          type="button"
          className="button--quiet"
          onClick={() => navigate({ name: 'activity', groupId })}
        >
          Activity →
        </button>
      </p>

      <section className="card">
        <h2>{group.data?.group.name ?? 'Loading…'}</h2>
        {archived && (
          <p className="badge">Archived — it takes no new expenses, and its money still counts.</p>
        )}
        <MemberStack members={current} />
        {/*
          Who is in it, and the two ways somebody else gets in: a name — which
          makes a placeholder, in the ledger from the next expense onwards —
          and a link, which is how a placeholder later becomes somebody's own
          account without a cent moving. `src/components/People.tsx` says why
          the link is only ever shown once.
        */}
        <People groupId={groupId} members={members} archived={archived} />

        {/*
          Owner-only, and rendered only for one: `requireOwner` in
          `server/groups.ts` refuses a rename, a currency change or an archive
          from anybody else, so there is no control here worth showing a
          member who cannot press it.
        */}
        {isOwner && group.data && (
          <GroupSettings
            groupId={groupId}
            name={group.data.group.name}
            currency={group.data.group.currency}
            archivedAt={group.data.group.archivedAt}
            hasTransactions={hasTransactions}
          />
        )}

        <ul className="chips">
          {balances.data?.balances.map((row, index) => {
            const said = describeMember(
              row.displayName,
              row.amountCents,
              currency,
              row.userId === myId,
            )
            return (
              <li key={row.userId} style={staggerStyle(index)}>
                <AmountChip
                  direction={said.direction}
                  label={said.label}
                  cents={said.cents}
                  currency={currency}
                  name={row.userId === myId ? 'You' : row.displayName}
                />
                <span className="visually-hidden">
                  {said.sentence}
                  {row.left ? ' (has left the group)' : ''}
                </span>
              </li>
            )
          })}
        </ul>
        {balances.isPending && <p className="muted">Working out where everybody stands…</p>}
        {balances.isError && <p className="bad">{balances.error.message}</p>}

        {/*
          Somebody has recorded paying you and nothing here can know whether
          the money actually arrived — a bank transfer and a note across a
          table are both invisible to this app. So it asks, rather than
          asserting, and the balance has already moved either way: the money
          moved when it moved, and a balance that waited for this would be
          wrong until somebody opened their phone.
        */}
        {awaiting.data?.settlements.map((settlement) => (
          <div key={settlement.transactionId} className="nudge">
            <p className="nudge__what">
              {describeNudge(
                settlement.fromDisplayName,
                formatMoney(settlement.amountCents, settlement.currency),
                formatDay(settlement.occurredOn),
                settlement.projectName,
              )}
            </p>
            {/*
              Both answers, side by side. A question with one button is not a
              question, and "did it land?" with nothing but Yes underneath it
              left somebody whose money never arrived with no way to say so —
              their only recourse was to cancel a payment they had not
              recorded.
            */}
            <p className="nudge__answers">
              <button
                type="button"
                disabled={confirmSettlement.isPending || rejectSettlement.isPending}
                onClick={() => {
                  setRejecting(null)
                  confirmSettlement.mutate(settlement.transactionId)
                }}
              >
                Yes, it landed
                <span className="visually-hidden">
                  {' '}
                  — the payment from {settlement.fromDisplayName ?? 'somebody'}
                </span>
              </button>
              {/*
                And it asks twice, unlike Yes. Yes changes nothing anybody can
                see; No removes somebody else's payment and puts the debt back
                on them, which is the same weight as the Cancel further down
                this screen and is asked for the same way.

                Disabled in an archived group, where the server refuses it:
                rejecting moves money and an archived group takes no writes
                that do. Confirming still works there, deliberately — see
                `server/settlements.ts`.
              */}
              <button
                type="button"
                className="button--danger"
                disabled={archived || confirmSettlement.isPending || rejectSettlement.isPending}
                onClick={() => {
                  if (rejecting !== settlement.transactionId) {
                    setRejecting(settlement.transactionId)
                    return
                  }
                  setRejecting(null)
                  rejectSettlement.mutate(settlement.transactionId)
                }}
              >
                {rejecting === settlement.transactionId
                  ? 'It never arrived — press again'
                  : 'No, it did not'}
                <span className="visually-hidden">
                  {' '}
                  — the payment from {settlement.fromDisplayName ?? 'somebody'}
                </span>
              </button>
            </p>
            {rejecting === settlement.transactionId && (
              <p className="muted note nudge__aside">
                {describeRejection(
                  settlement.fromDisplayName,
                  formatMoney(settlement.amountCents, settlement.currency),
                )}
              </p>
            )}
            {rejectSettlement.isError &&
              rejectSettlement.variables === settlement.transactionId && (
                <p className="bad note nudge__aside">{rejectSettlement.error.message}</p>
              )}
          </div>
        ))}
        {confirmSettlement.isError && <p className="bad">{confirmSettlement.error.message}</p>}

        {/*
          Who pays whom, with the screen rather than behind it. This is the
          other question a group gets opened for, and the list is the answer to
          it: no panel, no button, no state deciding whether it is on show.
        */}
        <div className="plan">
          <h3>Who pays whom</h3>

          {plan.isPending && <p className="muted">Working it out…</p>}
          {plan.isError && <p className="bad">{plan.error.message}</p>}
          <ol className="plan__list">
            {plan.data?.payments.map((payment, index) => (
              <li
                key={`${payment.fromUserId}-${payment.toUserId}`}
                style={staggerStyle(index)}
              >
                <span className="plan__who">
                  <strong>
                    {payment.fromUserId === myId
                      ? 'You'
                      : (payment.fromDisplayName ?? payment.fromUserId)}
                  </strong>{' '}
                  {payment.fromUserId === myId ? 'pay' : 'pays'}{' '}
                  <strong>
                    {payment.toUserId === myId
                      ? 'you'
                      : (payment.toDisplayName ?? payment.toUserId)}
                  </strong>
                </span>
                <span className="plan__amount">{formatMoney(payment.amountCents, currency)}</span>
                {/*
                  It asks before it writes. The row above is a suggestion —
                  the amount that would clear the ledger, not an amount
                  anybody has handed over — so the button opens
                  `SettleUp`, where somebody says how much was actually
                  paid and when, and the write goes out from there. What it
                  writes is still an ordinary `repayment` down the same
                  optimistic path an expense takes: the payer paid, the
                  recipient owed, and the balances above move before the
                  request is answered. Anybody in the group may settle one,
                  because the person who has just been handed cash is as
                  likely to be the one holding a phone as the person who
                  handed it over.
                */}
                <button
                  type="button"
                  className="plan__settle"
                  disabled={archived || fileExpense.isPending}
                  aria-haspopup="dialog"
                  onClick={() => setSettling(payment)}
                >
                  Settle up
                  <span className="visually-hidden">
                    {' '}
                    this payment of {formatMoney(payment.amountCents, currency)}
                  </span>
                </button>
              </li>
            ))}
          </ol>
          {/*
            The one place the plan speaks for itself, and it says both counts
            whichever list is above it — "three payments instead of six" is
            what turns a rerouted payment from something this app did quietly
            into something it is showing you. `describePlanShape` in
            `src/lib/settle.ts` is where the words are.
          */}
          {plan.data && (
            <p className="muted note">
              {describePlanShape(plan.data.summary)}{' '}
              {plan.data.payments.length > 0 &&
                'These are suggestions, not things that have happened — nothing is written ' +
                  'until somebody presses Settle up and says what was paid.'}
            </p>
          )}
          {fileExpense.isError && <p className="bad note">{fileExpense.error.message}</p>}

          {/*
            The switch, and the sentence above it, are the same argument made
            twice. Simplification is what lets this screen say "pay Ben" to
            somebody who has never transacted with Ben, and an app that reroutes
            money without saying so is one people stop believing. So the count
            before and the count after are on the screen whichever way the
            switch is set, and the switch itself is the group's — everybody in
            it is looking at the same plan.

            Under the list rather than over it, now that the list is not
            something somebody asked for: the answer comes first, and the
            control that changes the shape of it comes after the sentence
            explaining what shape it is. And it is only drawn once there is a
            plan for it to be about — an empty group has nothing to simplify,
            and a checkbox about nothing is one more thing to read.
          */}
          {plan.data && plan.data.summary.directCount > 0 && (
            <>
              <label className="plan__switch plan__switch--under">
                <input
                  type="checkbox"
                  checked={simplify}
                  disabled={setSimplify.isPending}
                  onChange={(event) => setSimplify.mutate(event.target.checked)}
                />
                Simplify who pays whom
              </label>
              {setSimplify.isError && <p className="bad note">{setSimplify.error.message}</p>}
            </>
          )}
        </div>
      </section>

      {/*
        The dialog Settle up opens on, over the whole screen rather than
        inside the plan: it is one payment being answered for, and the list
        behind it is going to move the moment it is written.

        Writing it is the same optimistic mutation the button used to fire
        directly, with the amount and the day the dialog collected instead of
        the suggestion and today. It closes on the press rather than on the
        answer, because the write is optimistic — the settlement is already in
        the list underneath, and a dialog waiting for a request that has
        already shown its result is a dialog in the way. A failure puts the
        balances back and says so under the plan, where the button was.
      */}
      {settling !== null && (
        <SettleUp
          key={`${settling.fromUserId}-${settling.toUserId}`}
          payment={settling}
          currency={currency}
          myId={myId}
          busy={fileExpense.isPending}
          // The optional "for project" picker: a payment made from the
          // group's plan can still be tagged with what it was for. It is a
          // label — the payment moves the group's balances either way.
          projects={projects.data?.projects ?? []}
          onClose={() => setSettling(null)}
          onSettle={(settled, projectId) => {
            fileExpense.mutate({
              draft: settlementDraft({
                fromUserId: settling.fromUserId,
                toUserId: settling.toUserId,
                toDisplayName: settling.toDisplayName,
                amountCents: settled.amountCents,
                occurredOn: settled.occurredOn,
                projectId,
              }),
              // The group's own order, which is what an odd cent is broken by
              // everywhere else. There is no odd cent in a payment to one
              // person, and passing the order anyway is how this stays the
              // same call as the other one.
              members,
              idempotencyKey: newIdempotencyKey(),
            })
            setSettling(null)
          }}
        />
      )}

      {/*
        The composer. It is a card of its own, open, always — filing an expense
        is what this app is for, and it used to start with a tap that only ever
        revealed a form nobody was going to decline to fill in. `key` is what
        empties it again once one has been filed, because filing is optimistic
        and there is no panel left to close.

        Not drawn at all for an archived group, which takes no writes, or for
        one whose members have all left: there is nobody to owe anything.
      */}
      {group.data && !archived && current.length > 0 && (
        <section className="card">
          <h2>Add an expense</h2>
          <AddExpense
            key={`new-${composer}`}
            groupId={groupId}
            currency={currency}
            myId={myId}
            members={members}
            dismissLabel="Clear"
            onDone={() => setComposer((count) => count + 1)}
          />
        </section>
      )}

      <Projects
        groupId={groupId}
        archived={archived}
        transactions={transactions.data?.transactions ?? []}
        navigate={navigate}
      />

      <section className="card">
        <h2>Expenses</h2>
        {transactions.isPending && <p className="muted">Fetching the list…</p>}
        {transactions.isError && <p className="bad">{transactions.error.message}</p>}
        {transactions.data?.transactions.length === 0 && (
          <p className="muted">Nothing has been spent in this group yet.</p>
        )}

        {groupByMonth(transactions.data?.transactions ?? [], (row) => row.occurredOn).map(
          (section) => (
            <section key={section.monthKey} className="month">
              <h3 className="month__heading">{section.heading}</h3>
              <ul className="rows">
                {section.rows.map((row, index) => {
                  const stake = describeYourStake(myId, row.paid, row.owed, row.currency)
                  const payers = row.paid
                    .map((allocation) =>
                      allocation.userId === myId
                        ? 'You'
                        : (nameOf.get(allocation.userId) ?? allocation.userId),
                    )
                    .join(' and ')

                  const category = categoryFor(row.category)
                  const project = row.projectId ? projectOf.get(row.projectId) : undefined

                  return (
                    <li
                      key={row.id}
                      className={`row${row.pending ? ' row--pending' : ''}`}
                      style={staggerStyle(index)}
                    >
                      <span className="row__day">{formatDay(row.occurredOn)}</span>
                      <Avatar
                        userId={row.paid[0]?.userId ?? row.createdBy}
                        displayName={payers || '—'}
                        size="sm"
                      />
                      <span className="row__what">
                        <span className="row__description">
                          {row.description}
                          {category && (
                            <span className="pill">
                              <span aria-hidden="true">{category.glyph}</span>
                              {category.label}
                            </span>
                          )}
                          {row.kind !== 'expense' && (
                            <span className="pill">{row.kind === 'loan' ? 'loan' : 'settling up'}</span>
                          )}
                          {project && (
                            <button
                              type="button"
                              className="pill pill--project"
                              onClick={() =>
                                navigate({ name: 'project', groupId, projectId: project.id })
                              }
                            >
                              <span className="visually-hidden">In the project </span>
                              {project.name}
                            </button>
                          )}
                          {/*
                            Whether the person paid has said it arrived. It is
                            on the row rather than only in the nudge because
                            the payer needs to see it too — "I said I paid
                            them" and "they agreed it turned up" are different
                            states, and only one of them ends the argument.
                          */}
                          {row.kind === 'repayment' &&
                            row.pending !== true &&
                            (row.confirmedAt !== null ? (
                              <span className="pill pill--confirmed">
                                <span aria-hidden="true">✓</span>
                                Confirmed
                              </span>
                            ) : (
                              <span className="pill pill--pending">Not confirmed yet</span>
                            ))}
                          {row.receiptCount > 0 && (
                            <span className="pill pill--receipt">
                              <span aria-hidden="true">🧾</span>
                              {row.receiptCount === 1 ? 'Receipt' : `${row.receiptCount} receipts`}
                            </span>
                          )}
                          {/*
                            Two different sentences for one flag apiece, never
                            both at once: `queued` means there was no network
                            to even try this on, and saying "Sending…" about a
                            request that has not gone out would be the exact
                            lie this ticket exists not to tell. `src/lib/queue.ts`
                            is what actually moves a queued row on to "Sending…"
                            — this is only which word is true right now.
                          */}
                          {row.queued ? (
                            <span className="pill pill--pending">Queued — offline</span>
                          ) : (
                            row.pending && <span className="pill pill--pending">Sending…</span>
                          )}
                        </span>
                        <span className="row__who">
                          {payers} paid {formatMoney(row.amountCents, row.currency)}
                        </span>
                      </span>
                      {/*
                        `count={false}`: what one expense did to you in April
                        is not counting to anything. The balances above the
                        list are news and move; the list is the record, and
                        forty rows of digits spinning at once on the way past
                        is a slot machine rather than a ledger.
                      */}
                      <Amount
                        direction={stake.direction}
                        label={stake.label}
                        cents={stake.cents}
                        currency={row.currency}
                        count={false}
                      />
                      {/*
                        A row that has not been answered yet has no id on the
                        server, and an archived group takes no writes at all,
                        so there is nothing to open the sheet onto in either
                        case.
                      */}
                      <button
                        type="button"
                        className="button--quiet row__edit"
                        disabled={row.pending === true || archived}
                        aria-expanded={editing?.id === row.id}
                        onClick={() => setEditing(editing?.id === row.id ? null : row)}
                      >
                        Edit
                        <span className="visually-hidden"> {row.description}</span>
                      </button>
                      {/*
                        The receipt chip above says how many there are; this
                        opens them. A row still being filed has no id to hang a
                        file off, so it has no button either.
                      */}
                      <button
                        type="button"
                        className="button--quiet row__receipts"
                        disabled={row.pending === true}
                        aria-expanded={openReceipts === row.id}
                        onClick={() =>
                          setOpenReceipts(openReceipts === row.id ? null : row.id)
                        }
                      >
                        {row.receiptCount > 0 ? 'Receipts' : 'Attach'}
                        <span className="visually-hidden"> for {row.description}</span>
                      </button>

                      {/*
                        The undo for a payment somebody recorded — on the
                        settlement itself, which is where a person goes looking
                        for it, and only on a settlement. Correcting an expense
                        is Edit, above; "this payment never happened" is not a
                        correction, it is a removal, and the balances it moved
                        go back where they were.

                        It asks twice. Cancelling is one press away from Edit
                        and Attach, and it puts a debt back on somebody — the
                        same reason `src/screens/Recurring.tsx` asks twice
                        before it stops a series.
                      */}
                      {row.kind === 'repayment' && row.pending !== true && (
                        <button
                          type="button"
                          className="button--danger row__cancel"
                          disabled={archived || cancelSettlement.isPending}
                          onClick={() => {
                            if (cancelling !== row.id) {
                              setCancelling(row.id)
                              return
                            }
                            setCancelling(null)
                            cancelSettlement.mutate(row.id)
                          }}
                        >
                          {cancelling === row.id ? 'Cancel it — press again' : 'Cancel'}
                          <span className="visually-hidden">
                            {' '}
                            this payment of {formatMoney(row.amountCents, row.currency)}
                          </span>
                        </button>
                      )}
                      {/*
                        Said only while the question is open, and only when
                        there is an acknowledgement to contradict: somebody has
                        already told this app the money turned up, and taking
                        the payment away puts what it settled back on the
                        person who paid it. That is worth saying before the
                        second press rather than after it.
                      */}
                      {cancelling === row.id && row.confirmedAt !== null && (
                        <p className="muted note row__wide">
                          {row.confirmedBy === myId
                            ? 'You'
                            : (nameOf.get(row.confirmedBy ?? '') ?? 'The person paid')}{' '}
                          said this payment arrived. Cancelling it puts the balance back where it
                          was before the payment was recorded.
                        </p>
                      )}
                      {cancelSettlement.isError && cancelSettlement.variables === row.id && (
                        <p className="bad note row__wide">{cancelSettlement.error.message}</p>
                      )}

                      {/*
                        The correction, in the row it is correcting. Re-mounted
                        per expense — `key` — so opening a different row starts
                        the sheet from that row's own inputs rather than
                        leaving the last one's typing in the boxes.
                      */}
                      {editing?.id === row.id && group.data && (
                        <AddExpense
                          key={row.id}
                          groupId={groupId}
                          currency={currency}
                          myId={myId}
                          members={members}
                          editing={row}
                          onDone={() => setEditing(null)}
                        />
                      )}

                      {openReceipts === row.id && (
                        <Receipts
                          groupId={groupId}
                          transactionId={row.id}
                          description={row.description}
                          // Opening one goes to the group's receipt viewer,
                          // which is a route: the same picture full-bleed,
                          // with this expense under it and a back gesture that
                          // comes back here.
                          onOpen={(receiptId) =>
                            navigate({ name: 'receipt', groupId, receiptId })
                          }
                          storage={me.data?.storage ?? null}
                          // `can_upload_attachments` is a 0/1 limit and `null`
                          // is unlimited, exactly as everywhere else. While
                          // /api/me is still in flight the button is offered
                          // rather than hidden: the server is the enforcement,
                          // and hiding a control that is probably allowed is a
                          // worse guess than showing one that might be refused.
                          canUpload={allowsUploads(me.data?.limits.can_upload_attachments)}
                          archived={archived}
                        />
                      )}
                    </li>
                  )
                })}
              </ul>
            </section>
          ),
        )}
      </section>
    </>
  )
}
